import { API_BASE_URL } from "@/config";

export interface Article {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  date: string;
  author: string;
  category: string;
  image: string;
  readTime: string;
}

export function formatDate(dateString?: string): string {
  if (!dateString) return "Reciente";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

export function mapBulletinToArticle(bulletin: any): Article {
  return {
    id: bulletin.slug || bulletin.id,
    title: bulletin.title,
    content: bulletin.content || "",
    excerpt: bulletin.excerpt || bulletin.content?.slice(0, 180) + "...",
    date: formatDate(bulletin.published_at || bulletin.created_at),
    author: bulletin.author_name || "Equipo Legal AI",
    category: bulletin.category || "Actualizaciones",
    image: bulletin.image_url || "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop",
    readTime: bulletin.read_time || "4 min",
  };
}

export const articles: Article[] = [
  {
    id: "sentencia-t-323-24-ia-generativa-rama-judicial-colombia",
    title: "La Sentencia T-323/24 y el Futuro de la IA Generativa en la Rama Judicial de Colombia",
    content: `# La Sentencia T-323/24 y el Futuro de la IA Generativa en la Rama Judicial de Colombia

La **Corte Constitucional de Colombia**, mediante la emblemática **Sentencia T-323 de 2024**, fijó la primera línea jurisprudencial vinculante sobre el uso de tecnologías de **Inteligencia Artificial Generativa** en la administración de justicia y el ejercicio del derecho procesal.

---

> [!IMPORTANT]
> **El Principio del Juez Natural e Insubstituibilidad**: La Corte reafirmó de manera categórica que los modelos de lenguaje (LLM) como ChatGPT o asistentes de IA **jamás pueden reemplazar el criterio humano** en la valoración probatoria ni en la toma de decisiones judiciales. La IA debe actuar exclusivamente como un asistente de investigación o redacción preliminar.

---

## 🏛️ Antecedentes y Contexto

El debate surgió a raíz de decisiones donde operadores judiciales utilizaron herramientas de IA para apoyar la fundamentación de tutelas y procesos civiles. La Corte analizó cómo conciliar la **innovación tecnológica** con las garantías fundamentales del **Debido Proceso (Art. 29 C.P.)**.

![Justicia e Inteligencia Artificial en Colombia](https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1200&auto=format&fit=crop)

---

## 📋 Pautas Clave para Abogados y Jueces en Colombia

La sentencia estableció cuatro pilares éticos y operacionales:

1. **Transparencia Transversal**: Tanto los jueces como los abogados litigantes deben **declarar explícitamente** cuando hayan empleado herramientas de IA para sintetizar textos, buscar precedentes o estructurar alegatos.
2. **Verificación Humana Obligatoria**: Es deber del profesional verificar cada cita jurisprudencial o norma generada por la IA para prevenir las conocidas *"alucinaciones algorítmicas"*.
3. **Protección de Datos Sensibles**: Prohibición de ingresar datos personales o secretos procesales protegidos por la Ley 1581 de 2012 en plataformas de IA públicas.
4. **Igualdad de Armas**: Garantizar que el uso de tecnologías no genere un desequilibrio injustificado entre las partes dentro del proceso.

---

## ⚖️ Cuadro Comparativo: Usos Permitidos vs. Usos Prohibidos

| Ámbito de Aplicación | Usos Permitidos con IA | Usos Prohibidos / Riesgos |
| :--- | :--- | :--- |
| **Investigación Legal** | Resumen de líneas de la Corte Constitucional o Consejo de Estado | Citación de sentencias inexistentes sin verificación |
| **Redacción de Escritos** | Estructuración inicial de demandas o contestaciones | Delegar la decisión de fondo en el algoritmo |
| **Gestión de Expedientes** | Ingesta y clasificación automática de radicados | Subir información confidencial o sensible sin anonimizar |

---

> [!TIP]
> **Recomendación para Despachos Jurídicos**: Implementar políticas internas de uso ético de la IA y emplear plataformas jurídicas especializadas como **Mi Asistente Legal IA**, que garantizan la confidencialidad de la información y la verificación en fuentes oficiales de la Rama Judicial (CPNU, SAMAI y TYBA).
`,
    excerpt: "Análisis detallado sobre las pautas éticas y jurídicas establecidas por la Corte Constitucional colombiana para el uso de ChatGPT y modelos de lenguaje por jueces y abogados.",
    date: "27 de agosto de 2026",
    author: "Dra. Carolina Restrepo",
    category: "Tecnología Legal",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1200&auto=format&fit=crop",
    readTime: "6 min"
  },
  {
    id: "automatizacion-dosimetria-penal-liquidaciones-laborales-colombia",
    title: "Automatización de la Dosimetría Penal y Liquidación Laboral: Calculadoras Jurídicas con IA",
    content: `# Automatización de la Dosimetría Penal y Liquidación Laboral: Calculadoras Jurídicas con IA

La cuantificación de perjuicios civiles, la dosimetría de cuartos penales y el cálculo de indemnizaciones laborales han sido tradicionalmente tareas propensas a errores humanos y demoras operativas en los despachos colombianos.

---

## 💡 Innovación Algorítmica en la Práctica Jurídica

Nuestra nueva suite de **Calculadoras Jurídicas Automáticas** integra las reglas exactas del ordenamiento legal colombiano:

- **Código Sustantivo del Trabajo (Art. 64, 186, 249)**: Liquidación de Cesantías, Intereses, Prima de Servicios e Indemnizaciones por despido sin justa causa.
- **Sistema de Cuartos Penales (Ley 599 de 2000)**: Ponderación precisa de agravantes, atenuantes y la determinación automática del ámbito punitivo.
- **Indexación con IPC del DANE y Fórmulas del Consejo de Estado**: Actualización del valor del dinero en liquidaciones administrativas y civiles.

![Calculadoras Jurídicas Especializadas](https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop)

---

> [!NOTE]
> **Actualización en Tiempo Real**: Todas las variables económicas (Salario Mínimo SMLMV, Auxilio de Transporte, Tasas de Interés Bancario Corriente e Índices del DANE) se sincronizan automáticamente sin necesidad de mantenimiento por parte del abogado.

---

## 📊 Beneficios Medibles para la Firma de Abogados

| Métrica | Proceso Manual | Con Calculadoras IA |
| :--- | :--- | :--- |
| **Tiempo de Liquidación Laboral** | 45 minutos por empleado | **Menos de 2 segundos** |
| **Margen de Error Matemático** | 12% (fórmulas Excel desactualizadas) | **0% Garantizado** |
| **Formato de Salida** | Texto plano o tabla | **Reporte PDF / Excel listo para aportar como prueba** |
`,
    excerpt: "Cómo los algoritmos avanzados reducen a segundos el cálculo de cuartos penales, indemnizaciones del Art. 64 del CST e indexación de perjuicios con IPC del DANE.",
    date: "25 de agosto de 2026",
    author: "Dr. Mateo Bermúdez",
    category: "Nuevas Funciones",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop",
    readTime: "5 min"
  },
  {
    id: "ley-1581-proteccion-datos-sistemas-ia-colombia",
    title: "Protección de Datos Personales (Ley 1581) y Sistemas de IA en Colombia: Guía para Despachos",
    content: `# Protección de Datos Personales (Ley 1581) y Sistemas de IA en Colombia: Guía para Despachos

El manejo de expedientes judiciales e información de clientes involucra datos altamente sensibles (salud, estados financieros, antecedentes procesales). Ante el auge de la IA, la **Superintendencia de Industria y Comercio (SIC)** ha incrementado la vigilancia sobre los responsables y encargados del tratamiento de datos en Colombia.

---

## 🔒 Aspectos Clave de Cumplimiento

> [!WARNING]
> **Riesgos del Uso de Herramientas Gratuitas**: El uso de motores de IA comerciales sin acuerdos de procesamiento de datos o cláusulas de confidencialidad puede constituir una violación grave del secreto profesional y de los principios de la Ley 1581 de 2012.

---

### 1. Consentimiento Informado y Privacidad desde el Diseño
Los despachos deben actualizar sus políticas de tratamiento de datos personales para incluir explícitamente el uso de tecnologías de IA en la gestión de expedientes.

### 2. Anonimización Proactiva
Antes de procesar documentos en modelos de IA, es indispensable aplicar técnicas de **anonimización o seudonimización** para remover nombres de partes, números de cédula y direcciones.

![Seguridad de Datos e Inteligencia Artificial](https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop)

---

## 🛠️ Checklist de Seguridad para Despachos Colombianos

- [x] Contar con Política de Tratamiento de Datos actualizada conforme al Decreto 1377 de 2013.
- [x] Utilizar plataformas con encriptación de datos en tránsito (TLS 1.3) y en reposo (AES-256).
- [x] Verificar que los datos de los clientes no sean utilizados para entrenar modelos públicos de IA.
- [x] Registrar las bases de datos en el RNBD (Registro Nacional de Bases de Datos) de la SIC cuando aplique.
`,
    excerpt: "Requisitos de la Superintendencia de Industria y Comercio (SIC) para el tratamiento de datos sensibles y confidenciales en asistentes legales con IA.",
    date: "20 de agosto de 2026",
    author: "Dr. Alejandro Gómez",
    category: "Privacidad y Cumplimiento",
    image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop",
    readTime: "7 min"
  },
  {
    id: "ingesta-masiva-expedientes-samai-cpnu-tyba-colombia",
    title: "Ingesta Masiva de Expedientes Judiciales (SAMAI, CPNU, TYBA): La Revolución del Monitoreo en Colombia",
    content: `# Ingesta Masiva de Expedientes Judiciales (SAMAI, CPNU, TYBA): La Revolución del Monitoreo en Colombia

Monitorear decenas o cientos de procesos judiciales de manera manual en los diversos portales de la Rama Judicial (SAMAI del Consejo de Estado, CPNU de la Judicatura o TYBA) consume valiosas horas de trabajo semanal.

---

## 🚀 Carga de Hasta 1,000 Radicados por Archivo

Con la nueva función de **Ingesta Masiva**, los despachos pueden cargar masivamente sus inventarios de litigios en segundos a través de archivos **Excel (.xlsx)** o **CSV (.csv)**.

> [!TIP]
> **Validación Automática de 23 Dígitos**: El sistema valida automáticamente la estructura del número único de radicación (NURJ) de 23 dígitos y clasifica el origen del portal correspondiente.

![Ingesta Masiva de Procesos Judiciales](https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop)

---

## 📝 Pasos Sencillos para la Importación

1. **Descarga la Plantilla Oficial**: Desde el módulo de *Despachos / Consulta de Procesos*, descarga la plantilla preformateada.
2. **Diligencia tus Procesos**: Copia tus radicados y especifica la fuente (\`SAMAI\`, \`CPNU\`, \`TYBA\` u \`OTRO\`).
3. **Arrastra y Carga**: Sube el archivo y deja que el motor procese las actuaciones e ilumine las alertas procesales en segundo plano.
`,
    excerpt: "Optimiza la gestión de tus procesos en los portales oficiales de la Rama Judicial colombiana mediante la carga masiva de archivos Excel y CSV.",
    date: "15 de agosto de 2026",
    author: "Equipo Legal AI",
    category: "Actualizaciones",
    image: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop",
    readTime: "4 min"
  }
];

export async function fetchBlogArticles(apiClient?: any): Promise<Article[]> {
  try {
    let data: any = null;
    
    if (apiClient && typeof apiClient.get === "function") {
      const res = await apiClient.get("/api/v1/bulletins");
      if (res.ok && Array.isArray(res.data?.bulletins)) {
        data = res.data.bulletins;
      }
    }

    if (!data) {
      const baseUrl = (API_BASE_URL || (window.location.origin.includes("localhost") ? "http://localhost:8001" : window.location.origin)).replace(/\/$/, "");
      const res = await fetch(`${baseUrl}/api/v1/bulletins`);
      if (res.ok) {
        const json = await res.json();
        if (Array.isArray(json.bulletins)) {
          data = json.bulletins;
        }
      }
    }

    if (data && data.length > 0) {
      return data.map(mapBulletinToArticle);
    }
  } catch (err) {
    console.warn("Could not fetch bulletins from backend API, using local fallbacks:", err);
  }

  return articles;
}

export async function fetchBlogArticleBySlugOrId(idOrSlug: string, apiClient?: any): Promise<Article | null> {
  try {
    let bulletin: any = null;

    if (apiClient && typeof apiClient.get === "function") {
      const res = await apiClient.get(`/api/v1/bulletins/${encodeURIComponent(idOrSlug)}`);
      if (res.ok && res.data?.bulletin) {
        bulletin = res.data.bulletin;
      }
    }

    if (!bulletin) {
      const baseUrl = (API_BASE_URL || (window.location.origin.includes("localhost") ? "http://localhost:8001" : window.location.origin)).replace(/\/$/, "");
      const res = await fetch(`${baseUrl}/api/v1/bulletins/${encodeURIComponent(idOrSlug)}`);
      if (res.ok) {
        const json = await res.json();
        if (json.bulletin) {
          bulletin = json.bulletin;
        }
      }
    }

    if (bulletin) {
      return mapBulletinToArticle(bulletin);
    }
  } catch (err) {
    console.warn(`Could not fetch bulletin details for ${idOrSlug}, searching fallback array:`, err);
  }

  return articles.find((a) => a.id === idOrSlug || a.id.toLowerCase() === idOrSlug.toLowerCase()) || null;
}
